import { Modifier, EditorState, SelectionState, convertToRaw } from 'draft-js';
import uuid from 'uuid';
import { toJS } from 'mobx';

export const getSelectionForEntity = (
  editorState,
  entityKey,
  startOnly = false,
) => {
  let anchorKey;
  let focusKey;
  let anchorOffset;
  let focusOffset;
  let first = true;
  const contentState = editorState.getCurrentContent();
  contentState.getBlockMap().forEach(block => {
    block.findEntityRanges(
      char => char.entity === entityKey,
      (start, end) => {
        if (first) {
          anchorKey = block.getKey();
          anchorOffset = start;
        }
        focusOffset = end;
        focusKey = block.getKey();
        first = false;
      },
    );
  });

  if (startOnly) {
    return SelectionState.createEmpty(anchorKey).merge({
      anchorKey,
      anchorOffset,
      focusOffset: anchorOffset,
    });
  }
  return SelectionState.createEmpty(anchorKey).merge({
    anchorKey,
    anchorOffset,
    focusKey,
    focusOffset,
  });
};

const removeOldLinksFromPage = editorState => {
  const content = editorState.getCurrentContent();
  let cleanContent = content;
  content.getBlockMap().forEach(block => {
    block.findEntityRanges(
      character => {
        if (character.getEntity() === null) return null;
        if (content.getEntity(character.getEntity()).getType() === 'LINK')
          return true;
        return null;
      },
      (start, end) => {
        const blockKey = block.getKey();
        const selection = SelectionState.createEmpty(blockKey).merge({
          anchorKey: blockKey,
          focusKey: blockKey,
          anchorOffset: start,
          focusOffset: end,
        });
        cleanContent = Modifier.applyEntity(cleanContent, selection, null);
      },
    );
  });
  const cleanEditorState = EditorState.push(
    editorState,
    cleanContent,
    'apply-entity',
  );
  return cleanEditorState;
};

// the duplicate datom bug is causing trouble because
// the backend datamodel expected entities to be part of
// a contentblock — therefore it doesnt accept entities
// that have multiple ranges in the page. this function
// will look for all entities that have multiple ranges
// and clone those entities in order to avoid duplicate
// datoms
const ensureUniqueRangesForEntities = editorState => {
  const content = editorState.getCurrentContent();
  const entities = {};
  let foundEntity = null;
  // go through the whole content and collect all entities
  content.getBlockMap().forEach(block => {
    block.findEntityRanges(
      character => {
        const entityKey = character.getEntity();
        if (!entityKey) return false;
        foundEntity = {
          entity: content.getEntity(entityKey),
          entityKey,
          blockKey: block.getKey(),
        };
        return true;
      },
      (start, end) => {
        const { entityKey, blockKey, ...restEntity } = foundEntity;
        if (entities[entityKey]) {
          entities[entityKey] = {
            ...entities[entityKey],
            ranges: [
              ...entities[entityKey].ranges,
              {
                start,
                end,
                blockKey,
              },
            ],
          };
        } else {
          entities[entityKey] = {
            ...restEntity,
            ranges: [
              {
                start,
                end,
                blockKey,
              },
            ],
          };
        }
      },
    );
  });
  // filter only the entities that have
  // more than one range associated with
  const multiRangeEntities = Object.keys(entities).reduce((acc, key) => {
    const entity = entities[key];
    if (entity.ranges.length > 1) {
      acc.push({ ...entity, key });
    }
    return acc;
  }, []);
  let cleanContent = content;
  // now go through all the multi-range entities
  // and clone them
  multiRangeEntities.forEach(entity => {
    // we can keep the first entity range as it is
    // therefore we start our loop at '1'
    for (let i = 1; i < entity.ranges.length; i += 1) {
      const range = entity.ranges[i];
      // create a selection for each entity range
      const selection = SelectionState.createEmpty(range.blockKey).merge({
        anchorKey: range.blockKey,
        focusKey: range.blockKey,
        anchorOffset: range.start,
        focusOffset: range.end,
      });
      // remove the entity range from the contenstate
      cleanContent = Modifier.applyEntity(cleanContent, selection, null);
      // create a new entity based on the same entity
      // BUT remove the squuid since we want the backend
      // to actually create a new database entity
      const entityType = entity.entity.get('type');
      const entityMutability = entity.entity.get('mutability');
      const entityData = entity.entity.get('data');
      delete entityData.squuid;
      cleanContent = cleanContent.createEntity(
        entityType,
        entityMutability,
        entityData,
      );
      const newEntityKey = cleanContent.getLastCreatedEntityKey();
      // apply the just created entity to the range we removed the
      // entity before
      cleanContent = Modifier.applyEntity(
        cleanContent,
        selection,
        newEntityKey,
      );
    }
  });
  const cleanEditorState = EditorState.push(
    editorState,
    cleanContent,
    'apply-entity',
  );
  return cleanEditorState;
};

// To be refactored in an external util file
export const populateBlocksWithUUIDs = (currentBlocks, lastStateOfBlocks) => {
  const populatedBlocks = currentBlocks.map(block => {
    const lastStateOfBlock = lastStateOfBlocks.find(b => b.key === block.key);
    if (lastStateOfBlock && lastStateOfBlock.uuid) {
      block.uuid = lastStateOfBlock.uuid;
    } else {
      block.uuid = uuid.v1();
    }
    return block;
  });

  return populatedBlocks;
};

export const preparePageForSave = page => {
  let cleanEditorState = removeOldLinksFromPage(page.editorState);
  cleanEditorState = ensureUniqueRangesForEntities(cleanEditorState);
  const contentState = cleanEditorState.getCurrentContent();
  const rawContentState = convertToRaw(contentState);
  const currentBlocks = rawContentState.blocks;
  const populatedBlocks = populateBlocksWithUUIDs(currentBlocks, page.blocks);
  const blockOrder = populatedBlocks.reduce((acc, block, index) => {
    acc[block.uuid] = index;
    return acc;
  }, {});
  const jsPage = toJS(page);
  return {
    blocks: populatedBlocks,
    entityMap: rawContentState.entityMap,
    blockOrder,
    data: jsPage.data,
    publishedAt: jsPage.publishedAt,
    tags: jsPage.tags,
    state: jsPage.state,
    uuid: jsPage.uuid,
    type: jsPage.type,
    title: jsPage.title,
    viewAccess: jsPage.viewAccess,
  };
};
